import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { Button, Grid2, Typography } from "@mui/material";
import classNames from "classnames";
import moment from "moment-timezone";
import Calendar from "react-calendar";

import { TMyClass } from "../../../../../@types";
import { defaultTimezone } from "../../../../../constants/timezone";
import { useSchedule } from "../../../../../hooks/useSchedule";
import { useTeachingPolicy } from "../../../../../hooks/useTeachingPolicy";
import { formatHourMinute, formatTwoDigit } from "../../../../../utils";

export type TBookingLessonProps = {
	booking: TMyClass;
	setSelectedBooking: Dispatch<SetStateAction<TMyClass | null>>;
};

export function BookingLesson({
	booking,
	setSelectedBooking,
}: TBookingLessonProps) {
	const { availableHours, policy } = useTeachingPolicy();
	const { bookings, busyTimes, reschedule } = useSchedule();
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<number | null>(null);
	const lesson = booking.lesson;

	const availableTimeSlots = useMemo(() => {
		if (!selectedDate) {
			return [];
		}

		if (!policy) {
			return [];
		}

		const timeBreak = policy.timeBreak;

		const lessonBlockTime = lesson.blockTime ?? 0;
		const dayOfWeek = selectedDate.getDay();

		const availableHoursOfDay = availableHours.filter(
			(availableHour) => availableHour.dayOfWeek === dayOfWeek,
		);

		if (availableHoursOfDay.length === 0) {
			return [];
		}

		const minuteArray = Array.from({ length: 24 * 60 + 1 }).fill(false);

		for (let i = 0; i < availableHoursOfDay.length; i++) {
			const startInMinute =
				availableHoursOfDay[i].startHour * 60 +
				availableHoursOfDay[i].startMinute;
			let endInMinute =
				availableHoursOfDay[i].endHour * 60 + availableHoursOfDay[i].endMinute;
			if (endInMinute === 0) {
				endInMinute = 24 * 60;
			}

			if (startInMinute === 0) {
				minuteArray[0] = true;
			}
			for (let j = startInMinute + 1; j <= endInMinute - lessonBlockTime; j++) {
				minuteArray[j] = true;
			}
		}

		[...bookings, ...busyTimes.map((time) => ({ ...time, id: 0, lessonId: 0 }))]
			.filter((bookingItem) => {
				if (bookingItem.id === booking.id) {
					return false;
				}
				const lessonStartTime = new Date(
					moment(bookingItem.startTime)
						.tz(lesson.course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				const lessonEndTime = new Date(
					moment(bookingItem.endTime)
						.tz(lesson.course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);

				if (
					lessonEndTime.getTime() <
					new Date(selectedDate.toDateString()).getTime()
				) {
					return false;
				}
				if (
					lessonStartTime.getTime() >
					new Date(selectedDate.toDateString()).getTime() + 86400000
				) {
					return false;
				}
				return true;
			})
			.forEach((bookingItem) => {
				const lessonStartTime = new Date(
					moment(bookingItem.startTime)
						.tz(lesson.course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				let startTimeToMinute =
					lessonStartTime.getHours() * 60 + lessonStartTime.getMinutes();
				if (lessonStartTime.toDateString() !== selectedDate.toDateString()) {
					startTimeToMinute = 0;
				}
				startTimeToMinute = Math.max(
					0,
					startTimeToMinute - timeBreak - lessonBlockTime,
				);

				const lessonEndTime = new Date(
					moment(bookingItem.endTime)
						.tz(lesson.course.timezoneName ?? defaultTimezone)
						.format("YYYY-MM-DD HH:mm:ss.SSS"),
				);
				let endTimeToMinute =
					lessonEndTime.getHours() * 60 + lessonEndTime.getMinutes();
				if (lessonEndTime.toDateString() !== selectedDate.toDateString()) {
					endTimeToMinute = 24 * 60;
				}
				endTimeToMinute = Math.min(24 * 60, endTimeToMinute + timeBreak);

				if (startTimeToMinute === 0) {
					minuteArray[0] = false;
				}

				for (let i = startTimeToMinute + 1; i <= endTimeToMinute; i++) {
					minuteArray[i] = false;
				}
			});

		const timeRanges = [];
		let start = null;
		for (let i = 0; i < minuteArray.length; i++) {
			if (minuteArray[i] && start === null) {
				if (i === 0) {
					start = i;
				} else {
					start = i - 1;
				}
			} else if (!minuteArray[i] && start !== null) {
				timeRanges.push({
					startTime: start,
					endTime: i - 1,
				});
				start = null;
			}
		}
		if (start !== null) {
			timeRanges.push({
				startTime: start,
				endTime: 24 * 60,
			});
		}

		const result = [];

		for (let i = 0; i < timeRanges.length; i++) {
			for (let j = timeRanges[i].startTime; j <= timeRanges[i].endTime; j++) {
				if (j % 15 === 0) {
					result.push(j);
				}
			}
		}

		return result;
	}, [
		availableHours,
		booking.id,
		bookings,
		busyTimes,
		lesson.blockTime,
		lesson.course.timezoneName,
		policy,
		selectedDate,
	]);

	useEffect(() => {
		setSelectedDate(null);
		setSelectedTimeSlot(null);
	}, [booking]);

	const onClickBookASlot = async () => {
		if (!selectedDate || selectedTimeSlot === null) {
			return;
		}

		const startDate = moment.tz(
			`${moment(selectedDate).format("YYYY-MM-DD")} ${formatTwoDigit(Math.floor(selectedTimeSlot / 60))}:${formatTwoDigit(selectedTimeSlot % 60)}`,
			lesson.course.timezoneName ?? defaultTimezone,
		);
		const endDate = moment.tz(
			moment(startDate).add(lesson.blockTime, "minute"),
			lesson.course.timezoneName ?? defaultTimezone,
		);

		const body = {
			startDate: startDate.toISOString(),
			lessonId: lesson.id,
		};

		const success = await reschedule(booking.id, body, {
			...booking,
			startTime: startDate.toISOString(),
			endTime: endDate.toISOString(),
		});

		if (success) {
			setSelectedBooking(null);
		}
	};

	return (
		<>
			<Typography className="normal-text">
				{`SELECT DATE & TIME - ${lesson.title}`}
			</Typography>
			{lesson.course.timezoneName !== null && (
				<Typography className="normal-text">
					{`${lesson.course.timezoneName} time`}
				</Typography>
			)}
			<Grid2 container spacing={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }}>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
					<Typography className="select-date-time normal-text">
						Select date
					</Typography>
					<Calendar
						onChange={(value) => {
							if (
								value instanceof Date &&
								value.toDateString() !== selectedDate?.toDateString()
							) {
								setSelectedTimeSlot(null);
								setSelectedDate(value);
							}
						}}
						value={selectedDate}
						minDate={new Date(new Date().getTime() + 86400000)}
						className="normal-text"
						locale="en-US"
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
					{selectedDate && (
						<>
							<Typography className="select-date-time normal-text">
								Select start time
							</Typography>
							{availableTimeSlots.length === 0 && (
								<Typography className="normal-text">
									No available booking slots
								</Typography>
							)}
							{availableTimeSlots.length > 0 && (
								<>
									<Grid2
										container
										spacing={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 2 }}>
										{availableTimeSlots.map((timeSlot) => (
											<Grid2
												key={timeSlot}
												size={{ xs: 6, sm: 6, md: 3, lg: 3, xl: 3 }}>
												<div
													className={classNames("hour-item", {
														"selected-hour": timeSlot === selectedTimeSlot,
													})}
													onClick={() => {
														setSelectedTimeSlot(timeSlot);
													}}>
													<Typography className="normal-text">
														{formatHourMinute(
															Math.floor(timeSlot / 60),
															timeSlot % 60,
														)}
													</Typography>
												</div>
											</Grid2>
										))}
									</Grid2>
									<div className="book-button-container">
										<Button
											className="normal-button disabled-button no-transform"
											onClick={() => {
												setSelectedBooking(null);
											}}>
											Cancel
										</Button>
										<Button
											className={classNames("normal-button no-transform", {
												"primary-button": selectedTimeSlot !== null,
												"disabled-button": selectedTimeSlot === null,
											})}
											disabled={selectedTimeSlot === null}
											onClick={onClickBookASlot}>
											Book
										</Button>
									</div>
								</>
							)}
						</>
					)}
				</Grid2>
			</Grid2>
		</>
	);
}
